import { memo, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Card,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import NoneFound from 'src/components/none-found';
import { SearchCard } from 'src/components/search-card';
import { SortableHeader } from 'src/components/sortable-header';
import { TruxTableCell } from 'src/components/trux-table-cell';
import { EldDriverStatusLabel } from 'src/components/eld-driver-status-label';
import { useSort } from 'src/hooks/use-sort';
import { useSearch } from 'src/hooks/use-search';
import { Constants } from 'src/constants';
import { TrackRow, Trip } from '../types';
import { latenessColor, latenessText, tripStatusColor, tripStatusText } from '../trip-utils';

export type BoardLine = {
  eldDriverId: number;
  driverName: string;
  unitNumber: string;
  dutyStatus: string;
  connected: boolean;
  tripName: string | null;
  tripStatus: number | null;
  progressPercent: number | null;
  lateHours: number | null;
  nextStopText: string | null;
  nextStopEta: string | null;
  nextTripName: string | null;
  nextTripDepartAt: string | null;
  docsText: string;
  inMyFleet: boolean;
  raw: TrackRow;
};

type BoardFilter = 'all' | 'onTrip' | 'idle' | 'late';

export type FleetScope = 'mine' | 'all';

type SegmentedOption<T extends string> = {
  value: T;
  label: string;
  count?: number;
  // paints the count red while inactive, so trouble stays visible without opening the filter
  alert?: boolean;
};

// the board's filter control: a pill segment per state, each carrying the live
// count of drivers behind it
function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        borderRadius: 5,
        bgcolor: 'background.paper',
        border: '1px solid #e5e6e6',
        flexShrink: 0,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        const showAlert = !active && option.alert && (option.count ?? 0) > 0;
        return (
          <ButtonBase
            key={option.value}
            onClick={() => onChange(option.value)}
            sx={{
              px: 2,
              py: 1,
              gap: 1,
              borderRadius: 5,
              typography: 'body2',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: active ? 'common.white' : 'text.primary',
              bgcolor: active ? '#2096f3' : 'transparent',
              transition: (theme) =>
                theme.transitions.create(['background-color', 'color'], { duration: 150 }),
              '&:hover': { bgcolor: active ? '#1c87db' : 'action.hover' },
            }}
          >
            {option.label}
            {option.count !== undefined && (
              <Box
                component="span"
                sx={{
                  typography: 'caption',
                  fontWeight: 700,
                  lineHeight: 1,
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 5,
                  minWidth: 22,
                  textAlign: 'center',
                  bgcolor: (theme) => {
                    if (active) return 'rgba(255, 255, 255, 0.28)';
                    if (showAlert) return theme.palette.error.main;
                    return theme.palette.action.selected;
                  },
                  color: active || showAlert ? 'common.white' : 'text.primary',
                }}
              >
                {option.count}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

type TrackBoardProps = {
  lines: BoardLine[];
  timezone: string;
  fleetScope: FleetScope;
  setFleetScope: (scope: FleetScope) => void;
  showFleetToggle: boolean;
  onSelectDriver: (eldDriverId: number) => void;
  onEditTrip: (trip: Trip) => void;
  onRequestStatusChange: (trip: Trip, newStatus: number, driverName: string) => void;
};

function TrackBoardComponent({
  lines,
  timezone,
  fleetScope,
  setFleetScope,
  showFleetToggle,
  onSelectDriver,
  onEditTrip,
  onRequestStatusChange,
}: TrackBoardProps) {
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [menuState, setMenuState] = useState<{ anchor: HTMLElement; line: BoardLine } | null>(null);

  const { sortedList, sortData } = useSort(lines, 'lateHours', 'desc');
  const { searchText, setSearchText, filteredList } = useSearch(sortedList || [], [
    'driverName',
    'unitNumber',
    'docsText',
  ]);

  const scopedList = useMemo(
    () => (fleetScope === 'mine' ? filteredList.filter((line) => line.inMyFleet) : filteredList),
    [filteredList, fleetScope]
  );

  const counts = useMemo(
    () => ({
      all: scopedList.length,
      onTrip: scopedList.filter((line) => line.tripStatus !== null).length,
      idle: scopedList.filter((line) => line.tripStatus === null && !line.nextTripName).length,
      late: scopedList.filter((line) => (line.lateHours ?? 0) > 0).length,
      myFleet: filteredList.filter((line) => line.inMyFleet).length,
    }),
    [scopedList, filteredList]
  );

  const visibleList = useMemo(() => {
    if (filter === 'onTrip') {
      return scopedList.filter((line) => line.tripStatus !== null);
    }
    if (filter === 'idle') {
      return scopedList.filter((line) => line.tripStatus === null && !line.nextTripName);
    }
    if (filter === 'late') {
      return scopedList.filter((line) => (line.lateHours ?? 0) > 0);
    }
    return scopedList;
  }, [scopedList, filter]);

  const formatEta = (eta: string | null) =>
    eta ? DateTime.fromISO(eta).setZone(timezone).toFormat('LLL d, h:mm a') : null;

  if (lines.length === 0) {
    return (
      <NoneFound
        title="No active drivers"
        message="The board shows every active ELD driver, their current trip and what comes next."
      />
    );
  }

  return (
    <>
      <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="drivers" />

      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {showFleetToggle && (
          <Segmented<FleetScope>
            value={fleetScope}
            onChange={setFleetScope}
            options={[
              { value: 'mine', label: 'My Fleet', count: counts.myFleet },
              { value: 'all', label: 'All Drivers', count: filteredList.length },
            ]}
          />
        )}
        <Segmented<BoardFilter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All', count: counts.all },
            { value: 'onTrip', label: 'On a Trip', count: counts.onTrip },
            { value: 'idle', label: 'Idle', count: counts.idle },
            { value: 'late', label: 'Late', count: counts.late, alert: true },
          ]}
        />
      </Stack>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeader sortKey="driverName" sortData={sortData}>Driver</SortableHeader>
                <SortableHeader sortKey="unitNumber" sortData={sortData}>Truck</SortableHeader>
                <SortableHeader sortKey="dutyStatus" sortData={sortData}>Duty</SortableHeader>
                <SortableHeader sortKey="tripName" sortData={sortData}>Current Trip</SortableHeader>
                <SortableHeader sortKey="progressPercent" sortData={sortData}>Progress</SortableHeader>
                <SortableHeader sortKey="lateHours" sortData={sortData}>Behind</SortableHeader>
                <SortableHeader sortKey="nextStopEta" sortData={sortData}>Next Stop</SortableHeader>
                <SortableHeader sortKey="nextTripDepartAt" sortData={sortData}>Next Trip</SortableHeader>
                <SortableHeader sortKey="docsText" sortData={sortData}>Shipping Docs</SortableHeader>
                <TruxTableCell> </TruxTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleList.map((line) => (
                <TableRow
                  key={line.eldDriverId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onSelectDriver(line.eldDriverId)}
                >
                  <TruxTableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Tooltip title={line.connected ? 'Connected' : 'Disconnected'}>
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: line.connected ? 'success.main' : 'text.disabled',
                          }}
                        />
                      </Tooltip>
                      {line.driverName}
                    </Stack>
                  </TruxTableCell>
                  <TruxTableCell>{line.unitNumber || '—'}</TruxTableCell>
                  <TruxTableCell>
                    <EldDriverStatusLabel status={line.dutyStatus as any} />
                  </TruxTableCell>
                  <TruxTableCell>
                    {line.tripStatus !== null ? (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Label color={tripStatusColor(line.tripStatus)}>
                          {tripStatusText(line.tripStatus)}
                        </Label>
                        <Typography variant="body2" noWrap>
                          {line.tripName}
                        </Typography>
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TruxTableCell>
                  <TruxTableCell sx={{ minWidth: 120 }}>
                    {line.progressPercent !== null ? (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(line.progressPercent, 100)}
                          color={(line.lateHours ?? 0) > 0 ? 'warning' : 'success'}
                          sx={{ flexGrow: 1 }}
                        />
                        <Typography variant="caption">{Math.round(line.progressPercent)}%</Typography>
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TruxTableCell>
                  <TruxTableCell>
                    {line.tripStatus !== null ? (
                      <Label color={latenessColor(line.lateHours)}>{latenessText(line.lateHours)}</Label>
                    ) : (
                      '—'
                    )}
                  </TruxTableCell>
                  <TruxTableCell>
                    {line.nextStopText ? (
                      <>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {line.nextStopText}
                        </Typography>
                        {line.nextStopEta && (
                          <Typography variant="caption" color="text.secondary">
                            ETA {formatEta(line.nextStopEta)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </TruxTableCell>
                  <TruxTableCell>
                    {line.nextTripName ? (
                      <>
                        <Typography variant="body2" noWrap>
                          {line.nextTripName}
                        </Typography>
                        {line.nextTripDepartAt && (
                          <Typography variant="caption" color="text.secondary">
                            departs {formatEta(line.nextTripDepartAt)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </TruxTableCell>
                  <TruxTableCell>
                    {line.docsText ? (
                      <Tooltip title={line.docsText}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                          {line.docsText}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TruxTableCell>
                  <TruxTableCell align="right">
                    {(line.raw.currentTrip || line.raw.nextTrip) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuState({ anchor: e.currentTarget, line });
                        }}
                      >
                        <Iconify icon="eva:more-vertical-fill" />
                      </IconButton>
                    )}
                  </TruxTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu
        anchorEl={menuState?.anchor}
        open={!!menuState}
        onClose={() => setMenuState(null)}
      >
        {menuState?.line.raw.currentTrip && (
          <MenuItem
            onClick={() => {
              onEditTrip(menuState.line.raw.currentTrip!);
              setMenuState(null);
            }}
          >
            <Iconify icon="eva:edit-outline" sx={{ mr: 1 }} /> Replan Trip
          </MenuItem>
        )}
        {menuState?.line.raw.currentTrip && (
          <MenuItem
            onClick={() => {
              onRequestStatusChange(
                menuState.line.raw.currentTrip!,
                Constants.TRIP_STATUS_FINISHED,
                menuState.line.driverName
              );
              setMenuState(null);
            }}
          >
            <Iconify icon="eva:checkmark-circle-2-outline" sx={{ mr: 1 }} /> Finish Trip
          </MenuItem>
        )}
        {menuState?.line.raw.currentTrip && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              onRequestStatusChange(
                menuState.line.raw.currentTrip!,
                Constants.TRIP_STATUS_ABANDONED,
                menuState.line.driverName
              );
              setMenuState(null);
            }}
          >
            <Iconify icon="eva:close-circle-outline" sx={{ mr: 1 }} /> Abandon Trip
          </MenuItem>
        )}
        {menuState?.line.raw.nextTrip && !menuState?.line.raw.currentTrip && (
          <MenuItem
            onClick={() => {
              onRequestStatusChange(
                menuState.line.raw.nextTrip!,
                Constants.TRIP_STATUS_STARTED,
                menuState.line.driverName
              );
              setMenuState(null);
            }}
          >
            <Iconify icon="eva:play-circle-outline" sx={{ mr: 1 }} /> Start Next Trip Now
          </MenuItem>
        )}
        {menuState?.line.raw.nextTrip && (
          <MenuItem
            onClick={() => {
              onEditTrip(menuState.line.raw.nextTrip!);
              setMenuState(null);
            }}
          >
            <Iconify icon="eva:edit-outline" sx={{ mr: 1 }} /> Edit Next Trip
          </MenuItem>
        )}
        {menuState?.line.raw.nextTrip && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              onRequestStatusChange(
                menuState.line.raw.nextTrip!,
                Constants.TRIP_STATUS_ABANDONED,
                menuState.line.driverName
              );
              setMenuState(null);
            }}
          >
            <Iconify icon="eva:close-circle-outline" sx={{ mr: 1 }} /> Abandon Next Trip
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

const TrackBoard = memo(TrackBoardComponent);
export default TrackBoard;
